import { useEffect, useMemo, useState } from "react";

import { ClockIcon, CopyIcon, DeleteIcon, MapPinPlusIcon, PenLineIcon, PublishIcon } from "@/components/icons";
import { BanIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { EllipsisVerticalIcon, EyeIcon, FlaskConicalIcon, RotateCcwIcon } from "lucide-react";
import { ParkingRateStatusEnum, ParkingRateType } from "../payment-zone-area.type";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
  AlertDialogHeader,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";
import { GroupedRate } from "../pricing-configurator.types";
import {
  getDisplayName,
  getStatusBadge,
  getVersionLabel,
  groupRatesByLogicalId,
  toMajorUnit,
} from "../pricing-configurator.const";

type ParkingRateAsideProps = {
  open: boolean;
  parkingRates: ParkingRateType[];
  onAddClick: () => void;
  onEditClick: (rate: ParkingRateType) => void;
  onDuplicateClick: (rate: ParkingRateType) => void;
  onViewClick: (rate: ParkingRateType) => void;
  onDeleteClick: (rate: ParkingRateType) => void;
  onPublishClick: (rate: ParkingRateType) => void;
  onSetStateClick: (rate: ParkingRateType, status: ParkingRateStatusEnum) => void;
  onSimulateClick: (rate: ParkingRateType) => void;
  isLoading?: boolean;
};

export const ParkingRateAside = ({
  open,
  parkingRates,
  onAddClick,
  onEditClick,
  onDuplicateClick,
  onViewClick,
  onDeleteClick,
  onPublishClick,
  onSetStateClick,
  onSimulateClick,
  isLoading = false,
}: ParkingRateAsideProps) => {
  const [openConfirm, setOpenConfirm] = useState(false);
  const [rateToDelete, setRateToDelete] = useState<ParkingRateType | null>(null);
  const [selectedVersions, setSelectedVersions] = useState<Record<string, string>>({});

  const groupedRates = useMemo(() => groupRatesByLogicalId(parkingRates), [parkingRates]);

  useEffect(() => {
    setSelectedVersions({});
  }, [parkingRates]);

  const getSelectedRate = (group: GroupedRate): ParkingRateType => {
    const selectedId = selectedVersions[group.logicalRateId];
    if (selectedId) {
      const found = group.allVersions.find((v) => v.id === selectedId);
      if (found) return found;
    }
    return group.currentRate;
  };

  const rateCountLabel = useMemo(() => {
    const total = groupedRates.length;
    return total === 0 ? "Aucune tarification" : `${total} ${total > 1 ? "tarifications" : "tarification"}`;
  }, [groupedRates]);

  return (
    <>
      <aside
        className={cn(
          "fixed inset-y-4 right-2 z-50 flex w-88 flex-col rounded-xl border border-border bg-background shadow-xl transition-transform duration-200",
          open ? "translate-x-0 pointer-events-auto" : "pointer-events-none translate-x-[calc(100%+1.5rem)]",
        )}
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <p className="text-sm font-semibold">Tarifications</p>
            <p className="text-xs text-muted-foreground">{rateCountLabel}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="icon" onClick={onAddClick} title="Ajouter une tarification">
              <MapPinPlusIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <ScrollArea className="flex-1 min-h-0 px-5 py-4">
          <div className="space-y-3">
            {isLoading ? (
              <div className="text-sm text-muted-foreground text-center py-8">Chargement...</div>
            ) : groupedRates.length === 0 ? (
              <div className="flex h-32 items-center justify-center rounded-lg border border-dashed border-muted-foreground/30 text-center text-xs text-muted-foreground">
                Commencez par ajouter une tarification.
              </div>
            ) : (
              groupedRates.map((group) => {
                const rate = getSelectedRate(group);
                const hasMultipleVersions = group.allVersions.length > 1;
                const badge = getStatusBadge(rate.status);
                const canSimulate = !!rate.compiledPricingPoints?.length;

                const borderColor =
                  rate.status === ParkingRateStatusEnum.ENABLED
                    ? "border-l-green-500"
                    : rate.status === ParkingRateStatusEnum.DRAFT
                      ? "border-l-amber-400"
                      : "border-l-muted-foreground/30";

                return (
                  <div
                    key={group.logicalRateId}
                    className={cn(
                      "rounded-lg border border-border/80 border-l-[3px] bg-card px-4 py-3 shadow-sm transition-colors",
                      borderColor,
                    )}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <p className="min-w-0 wrap-anywhere text-sm font-semibold leading-snug">{getDisplayName(rate)}</p>
                      <span
                        className={cn(
                          "rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap shrink-0",
                          badge.className,
                        )}
                      >
                        {badge.label}
                      </span>
                    </div>

                    {/* Zone + tarif */}
                    <div className="flex items-center justify-between mb-3">
                      <p className="min-w-0 wrap-anywhere text-xs text-muted-foreground">{rate.zoneName}</p>
                      {rate.rateAmount != null && rate.rateUnit && (
                        <p className="text-xs font-semibold text-foreground shrink-0 ml-2">
                          {toMajorUnit(rate.rateAmount).toFixed(2)} € / {rate.rateUnit}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-1 mb-3">
                      <ClockIcon className="h-3 w-3  mt-[4px] text-muted-foreground shrink-0" />
                      {!rate.paidTimeRanges?.length ? (
                        <span className="text-xs text-muted-foreground">24h/24 · 7j/7</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {`${rate.paidTimeRanges.length} jour${rate.paidTimeRanges.length > 1 ? "s" : ""} configuré${rate.paidTimeRanges.length > 1 ? "s" : ""}`}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-between gap-2">
                      {hasMultipleVersions ? (
                        <Select
                          value={rate.id ?? ""}
                          onValueChange={(id) =>
                            setSelectedVersions((prev) => ({ ...prev, [group.logicalRateId]: id }))
                          }
                        >
                          <SelectTrigger size="sm" className="cursor-pointer text-xs max-w-[140px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {group.currentVersions.length > 0 && (
                              <SelectGroup>
                                <SelectLabel>Version courante</SelectLabel>
                                {group.currentVersions.map((v) => (
                                  <SelectItem key={v.id} value={v.id!} className="cursor-pointer text-xs">
                                    {getVersionLabel(v)}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            )}
                            {group.previousVersions.length > 0 && (
                              <SelectGroup>
                                <SelectLabel>Désactivé</SelectLabel>
                                {group.previousVersions.map((v) => (
                                  <SelectItem key={v.id} value={v.id!} className="cursor-pointer text-xs">
                                    {getVersionLabel(v)}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            )}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div />
                      )}

                      <div className="flex items-center gap-2 w-[120px] justify-end shrink-0">
                        {rate.status === ParkingRateStatusEnum.DRAFT && (
                          <Button size="sm" className="cursor-pointer gap-2" onClick={() => onPublishClick(rate)}>
                            <PublishIcon className="h-4 w-4" color="#FFFFFF" />
                            Publier
                          </Button>
                        )}
                        {rate.status === ParkingRateStatusEnum.DISABLED && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="cursor-pointer gap-2"
                            onClick={() => onSetStateClick(rate, ParkingRateStatusEnum.ENABLED)}
                          >
                            <RotateCcwIcon className="h-4 w-4" />
                            Réactiver
                          </Button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 cursor-pointer">
                              <EllipsisVerticalIcon className="h-4 w-4" />
                              <span className="sr-only">Actions</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {rate.status !== ParkingRateStatusEnum.DRAFT && (
                              <DropdownMenuItem className="cursor-pointer" onClick={() => onViewClick(rate)}>
                                <EyeIcon className="h-4 w-4" />
                                Voir
                              </DropdownMenuItem>
                            )}
                            {rate.status !== ParkingRateStatusEnum.DISABLED && (
                              <DropdownMenuItem className="cursor-pointer" onClick={() => onEditClick(rate)}>
                                <PenLineIcon className="h-4 w-4" />
                                {rate.status === ParkingRateStatusEnum.DRAFT ? "Modifier" : "Nouvelle version"}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem className="cursor-pointer" onClick={() => onDuplicateClick(rate)}>
                              <CopyIcon className="h-4 w-4" />
                              Dupliquer
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="cursor-pointer"
                              disabled={!canSimulate}
                              onClick={() => onSimulateClick(rate)}
                            >
                              <FlaskConicalIcon className="h-4 w-4" />
                              <div className="flex flex-col items-start">
                                <span>Tester</span>
                                {!canSimulate && (
                                  <span className="text-xs text-muted-foreground">
                                    Configurez d'abord la tarification
                                  </span>
                                )}
                              </div>
                            </DropdownMenuItem>
                            {rate.status === ParkingRateStatusEnum.ENABLED && (
                              <DropdownMenuItem
                                className="cursor-pointer"
                                variant="destructive"
                                onClick={() => onSetStateClick(rate, ParkingRateStatusEnum.DISABLED)}
                              >
                                <BanIcon className="h-4 w-4" />
                                Désactiver
                              </DropdownMenuItem>
                            )}
                            {rate.status !== ParkingRateStatusEnum.ENABLED &&
                              rate.status !== ParkingRateStatusEnum.DISABLED && (
                                <DropdownMenuItem
                                  className="cursor-pointer"
                                  variant="destructive"
                                  onClick={() => {
                                    setRateToDelete(rate);
                                    setOpenConfirm(true);
                                  }}
                                >
                                  <DeleteIcon className="h-4 w-4" />
                                  Supprimer
                                </DropdownMenuItem>
                              )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>
        <footer className=" rounded-b-xl  border-t border-border bg-background px-5 py-3 text-xs text-muted-foreground font-medium">
          Utilisez le bouton «
          <MapPinPlusIcon className="h-4 w-4 inline align-text-bottom font-semibold text-foreground" />» pour créer une
          tarification puis enregistrez.
        </footer>
      </aside>

      <AlertDialog
        open={openConfirm}
        onOpenChange={(open) => {
          if (!open) {
            setOpenConfirm(false);
            setRateToDelete(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Supprimer la tarification "{rateToDelete ? getDisplayName(rateToDelete) : ""}"
            </AlertDialogTitle>
            <AlertDialogDescription>Êtes-vous sûr de vouloir supprimer cette tarification ?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (rateToDelete) {
                  onDeleteClick(rateToDelete);
                }
                setOpenConfirm(false);
                setRateToDelete(null);
              }}
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
